// Thin wrapper around face-api.js. Lazy-loads models from /models on first use.
import * as faceapi from "face-api.js";

const MODELS_URL = "/models";
let modelsLoaded = false;
let loadPromise = null;

export const loadFaceModels = () => {
    if (modelsLoaded) return Promise.resolve();
    if (loadPromise) return loadPromise;
    loadPromise = Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODELS_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_URL),
    ]).then(() => {
        modelsLoaded = true;
    });
    return loadPromise;
};

const detectorOptions = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });

/** Compute Eye Aspect Ratio (EAR) for a 6-point eye contour. */
const eyeAspectRatio = (eye) => {
    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    const v1 = dist(eye[1], eye[5]);
    const v2 = dist(eye[2], eye[4]);
    const h = dist(eye[0], eye[3]);
    if (h === 0) return 0;
    return (v1 + v2) / (2 * h);
};

const EAR_OPEN_THRESHOLD = 0.21;

/** Detect a face + landmarks + descriptor. Returns { embedding, eyesOpen, score, leftEAR, rightEAR } or null. */
export const detectFace = async (source) => {
    await loadFaceModels();
    const result = await faceapi
        .detectSingleFace(source, detectorOptions)
        .withFaceLandmarks()
        .withFaceDescriptor();
    if (!result || !result.descriptor) return null;
    const landmarks = result.landmarks;
    const leftEAR = eyeAspectRatio(landmarks.getLeftEye());
    const rightEAR = eyeAspectRatio(landmarks.getRightEye());
    const eyesOpen = leftEAR >= EAR_OPEN_THRESHOLD && rightEAR >= EAR_OPEN_THRESHOLD;
    return {
        embedding: Array.from(result.descriptor),
        eyesOpen,
        leftEAR,
        rightEAR,
        score: result.detection?.score || 0,
    };
};

/** Compute embedding from a File (image upload). */
export const detectFaceFromFile = async (file) => {
    const img = await readImage(file);
    try {
        return await detectFace(img);
    } finally {
        URL.revokeObjectURL(img.src);
    }
};

const readImage = (file) =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (e) => reject(e);
        img.src = url;
    });

export const isModelsLoaded = () => modelsLoaded;
